import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

// Register all Chart.js components
Chart.register(...registerables)

export default class extends Controller {
  static targets = ["dailyUsage", "modelUsage", "formatUsage", "statusBreakdown", "responseTimes"]
  static values = {
    dailyUsage: Array,
    modelUsage: Object,
    formatUsage: Object,
    statusBreakdown: Object,
    responseTimes: Array
  }

  connect() {
    this.charts = {}
    this.initCharts()
  }

  disconnect() {
    // Destroy all charts on disconnect to prevent memory leaks
    Object.values(this.charts).forEach(chart => chart.destroy())
  }

  initCharts() {
    if (this.hasDailyUsageTarget && this.dailyUsageValue.length > 0) {
      this.initDailyUsageChart()
    }
    if (this.hasModelUsageTarget && Object.keys(this.modelUsageValue).length > 0) {
      this.initModelUsageChart()
    }
    if (this.hasFormatUsageTarget && Object.keys(this.formatUsageValue).length > 0) {
      this.initFormatUsageChart()
    }
    if (this.hasStatusBreakdownTarget && Object.keys(this.statusBreakdownValue).length > 0) {
      this.initStatusBreakdownChart()
    }
    if (this.hasResponseTimesTarget && this.responseTimesValue.length > 0) {
      this.initResponseTimesChart()
    }
  }

  initDailyUsageChart() {
    const ctx = this.dailyUsageTarget.getContext("2d")
    const data = this.dailyUsageValue
    
    this.charts.dailyUsage = new Chart(ctx, {
      type: "line",
      data: {
        labels: data.map(d => d[0]),
        datasets: [{
          label: "API Requests",
          data: data.map(d => d[1]),
          borderColor: "#8b5cf6",
          backgroundColor: "rgba(139, 92, 246, 0.1)",
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: "#8b5cf6",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          pointHoverRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: 12,
            titleFont: { size: 13 },
            bodyFont: { size: 12 }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { 
              stepSize: 1,
              font: { size: 11 }
            },
            grid: { color: "rgba(0, 0, 0, 0.05)" }
          },
          x: {
            ticks: { font: { size: 10 } },
            grid: { display: false }
          }
        }
      }
    })
  }

  initModelUsageChart() {
    const ctx = this.modelUsageTarget.getContext("2d")
    const data = this.modelUsageValue
    const colors = ["#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#6366f1"]
    
    this.charts.modelUsage = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: Object.keys(data).map(k => k.charAt(0).toUpperCase() + k.slice(1)),
        datasets: [{
          data: Object.values(data),
          backgroundColor: colors.slice(0, Object.keys(data).length),
          borderWidth: 0,
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "65%",
        plugins: {
          legend: {
            position: "right",
            labels: {
              usePointStyle: true,
              padding: 15,
              font: { size: 11 }
            }
          }
        }
      }
    })
  }

  initFormatUsageChart() {
    const ctx = this.formatUsageTarget.getContext("2d")
    const data = this.formatUsageValue
    const colors = ["#06b6d4", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#6366f1"]
    
    this.charts.formatUsage = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: Object.keys(data).map(k => k.toUpperCase()),
        datasets: [{
          data: Object.values(data),
          backgroundColor: colors.slice(0, Object.keys(data).length),
          borderWidth: 0,
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "65%",
        plugins: {
          legend: {
            position: "right",
            labels: {
              usePointStyle: true,
              padding: 15,
              font: { size: 11 }
            }
          }
        }
      }
    })
  }

  initStatusBreakdownChart() {
    const ctx = this.statusBreakdownTarget.getContext("2d")
    const data = this.statusBreakdownValue
    
    this.charts.statusBreakdown = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: Object.keys(data),
        datasets: [{
          data: Object.values(data),
          backgroundColor: ["#10b981", "#ef4444"],
          borderWidth: 0,
          hoverOffset: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              usePointStyle: true,
              padding: 15,
              font: { size: 11 }
            }
          }
        }
      }
    })
  }

  initResponseTimesChart() {
    const ctx = this.responseTimesTarget.getContext("2d")
    const data = this.responseTimesValue
    
    this.charts.responseTimes = new Chart(ctx, {
      type: "bar",
      data: {
        labels: data.map(d => d[0]),
        datasets: [{
          label: "Response Time (ms)",
          data: data.map(d => d[1]),
          backgroundColor: "rgba(6, 182, 212, 0.7)",
          borderColor: "#06b6d4",
          borderWidth: 1,
          borderRadius: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: 12
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: "ms", font: { size: 11 } },
            ticks: { font: { size: 10 } },
            grid: { color: "rgba(0, 0, 0, 0.05)" }
          },
          x: {
            ticks: { font: { size: 9 }, maxRotation: 45 },
            grid: { display: false }
          }
        }
      }
    })
  }
}
